import PageLayout from '@components/page/PageLayout'
import { Button, Checkbox, Flex, Table, Text, TextInput } from '@mantine/core'
import { useEffect, useState } from 'react'
import TableDeleteButton from '@components/button/TableDeleteButton'
import {
    IRequestDeleteUser,
    IResponseGetAllUsers,
    IResponseGetAllUsersRowData,
} from '@apiinterface/IUser'
import TableTemplate from '@components/table/TableTemplate'
import { ITHeadMap, ITable } from '@interfaces/ITable'
import { modals } from '@mantine/modals'
import { QueryObserverResult } from 'react-query'
import TablePagination from '@components/table/TablePagination'
import dayjs from '@config/dayjs'
import {
    IconCheck,
    IconExclamationCircle,
    IconTrash,
} from '@tabler/icons-react'
import api from '@config/api'
import { notifications } from '@mantine/notifications'
import User from '@api/User'
import TableEditButton from '@components/button/TableEditButton'
import InputUserRole from '@components/inputs/quiz/InputUserRole'
import TableAddNewButton from '@components/button/TableAddNewButton'
import FormCreateUser from '@components/form/FormCreateUser'
import FormEditUser from '@components/form/FormEditUser'
import TableKeyEditButton from '@components/button/TableKeyEditButton'
import FormEditUserPassword from '@components/form/FormEditUserPassword'

const AllUser = () => {
    const [selection, setSelection] = useState<number[]>([])
    const [limit, setlimit] = useState(10)
    const [orderby, setorderby] =
        useState<keyof IResponseGetAllUsersRowData>('id')
    const [order, setorder] = useState<string>('desc')
    const [page, setpage] = useState(1)
    const [search, setsearch] = useState('')
    const [role, setrole] = useState('')

    const lists = User.useGetAllUsers({
        limit,
        orderby,
        order,
        page,
        search,
        role,
    })

    useEffect(() => {
        lists.refetch()
        // eslint-disable-next-line
    }, [page, limit, order, orderby, role])

    const openBulkDeleteConfirmModal = () => {
        const data: IRequestDeleteUser = {
            ids: selection,
        }
        const request = async () => {
            try {
                const res = await api.delete('/users', { data: data })
                notifications.show({
                    title: res.data.message,
                    message: '',
                    color: 'teal',
                    icon: <IconCheck size='1rem' />,
                })
                lists.refetch()
                setSelection([])
            } catch (error: any) {
                notifications.show({
                    title: 'เกิดข้อผิดพลาด',
                    message: error.response?.data.message,
                    color: 'red',
                    icon: <IconExclamationCircle size='1rem' />,
                })
                setSelection([])
            }
        }
        modals.openConfirmModal({
            title: 'กรุณายืนยันการลบ',
            labels: { confirm: 'ยืนยัน', cancel: 'ยกเลิก' },
            children: <Text>ลบรายการ {selection.length} รายการ</Text>,
            onConfirm: () => {
                request()
            },
            modalId: 'deleteBulk',
        })
    }

    const thead: ITHeadMap<IResponseGetAllUsersRowData>[] = [
        {
            sortkey: 'id',
            label: 'ไอดี',
            width: '7%',
        },
        {
            label: 'ชื่อ-นามสกุล',
            width: '15%',
        },
        {
            sortkey: 'username',
            label: 'ชื่อผู้ใช้งาน',
            width: '15%',
        },
        {
            sortkey: 'email',
            label: 'อีเมล',
            width: '15%',
        },
        {
            sortkey: 'role_name',
            label: 'ชื่อสิทธิการใช้งาน',
            width: '10%',
        },
        {
            sortkey: 'last_login_at',
            label: 'เข้าสู่ระบบล่าสุด',
            width: '15%',
        },
        {
            label: 'Action',
            width: '15%',
        },
    ]

    return (
        <PageLayout pageTitle='ผู้ดูแลระบบ'>
            <Flex gap='md' direction={{ xs: 'column', lg: 'row' }}>
                <TextInput
                    onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                            console.log('Enter')
                            lists.refetch()
                        }
                    }}
                    w='100%'
                    value={search}
                    onChange={(event) => setsearch(event.target.value)}
                    placeholder='ค้นหา'
                    label='ค้นหารายการ'
                />
                <InputUserRole
                    label='ประเภทบุคคล'
                    withAll
                    miw='180px'
                    w='100%'
                    value={String(role)}
                    onChange={(value) => {
                        if (value !== String(role)) {
                            setrole(String(value))
                            setpage(1)
                        }
                    }}
                />
                <TableAddNewButton
                    label='เพิ่มผู้ใช้งาน'
                    onClick={() => {
                        modals.open({
                            title: (
                                <Text c='text.2' fw='bold'>
                                    สร้างผู้ใช้งาน
                                </Text>
                            ),
                            children: (
                                <FormCreateUser
                                    refresh={() => lists.refetch()}
                                />
                            ),
                        })
                    }}
                />
            </Flex>
            {selection.length ? (
                <Flex
                    mt='lg'
                    py='xs'
                    px='md'
                    align='center'
                    justify='space-between'
                    style={{ borderRadius: '10px' }}
                    bg='blue'
                >
                    <Text c='text.0'>เลือก {selection.length} รายการ</Text>
                    <Button
                        py={0}
                        onClick={openBulkDeleteConfirmModal}
                        leftSection={<IconTrash size={14} />}
                        variant='filled'
                    >
                        ลบ
                    </Button>
                </Flex>
            ) : (
                ''
            )}
            <TableTemplate<IResponseGetAllUsersRowData>
                thead={thead}
                KeyID={'id'}
                orderby={orderby}
                order={order}
                setorder={setorder}
                setorderby={setorderby}
                lists={lists?.data?.lists}
                setSelection={setSelection}
                selection={selection}
            >
                {lists?.data?.lists.map((value) => (
                    <AllUserTableRow
                        selection={selection}
                        setSelection={setSelection}
                        key={value.id}
                        data={value}
                        refreshTable={lists.refetch}
                    />
                ))}
            </TableTemplate>
            {lists.data?.lists.length ? (
                <TablePagination
                    limit={lists.data?.lists.length}
                    setlimit={setlimit}
                    setpage={setpage}
                    page={page}
                    totalPages={lists.data.totalPages}
                    totalRows={lists.data.totalRows}
                />
            ) : (
                <></>
            )}
        </PageLayout>
    )
}

const AllUserTableRow = ({
    data,
    refreshTable,
    selection,
    setSelection,
}: {
    data: IResponseGetAllUsersRowData
    selection: ITable<IResponseGetAllUsersRowData>['selection']
    setSelection: ITable<IResponseGetAllUsersRowData>['setSelection']
    refreshTable: () => Promise<
        QueryObserverResult<IResponseGetAllUsers, unknown>
    >
}) => {
    const reqDeleteProjectVendor = User.useDeleteUserByID({
        user_id: data.id,
        aftersuccess: () => {
            refreshTable()
            setSelection([])
        },
    })
    const toggleRow = (id: number) =>
        setSelection((current) =>
            current.includes(id)
                ? current.filter((item) => item !== id)
                : [...current, id]
        )
    const openDeleteConfirmModal = () =>
        modals.openConfirmModal({
            title: 'กรุณายืนยันการลบ',
            labels: { confirm: 'ยืนยัน', cancel: 'ยกเลิก' },
            children: (
                <Text>
                    ลบผู้ใช้งานคุณ {data.first_name} {data.last_name}
                    {data.id}
                </Text>
            ),
            onConfirm: reqDeleteProjectVendor.onSubmit,
        })

    const openEditUserModal = (
        olddata: IResponseGetAllUsersRowData,
        user_id: number
    ) =>
        modals.open({
            title: 'แก้ไขข้อมูลผู้ใช้งาน',

            children: (
                <FormEditUser
                    olddata={olddata}
                    user_id={user_id}
                    refresh={refreshTable}
                />
            ),
        })

    const openEditUserPasswordModal = (user_id: number) =>
        modals.open({
            title: 'แก้ไขข้อมูลผู้ใช้งาน',

            children: (
                <FormEditUserPassword
                    user_id={user_id}
                    refresh={refreshTable}
                />
            ),
        })

    enum Role {
        'SUPER ADMIN' = 1,
        'ADMIN',
        'STAFF',
    }

    return (
        <tr key={data.id}>
            <Table.Td>
                <Checkbox
                    checked={selection.includes(Number(data.id))}
                    onChange={() => toggleRow(Number(data.id))}
                />
            </Table.Td>
            <td>{data.id}</td>
            <td>
                {data.first_name} {data.last_name}
            </td>
            <td>{data.username}</td>
            <td>{data.email}</td>
            <td>{Role[data.role_id]}</td>
            <td>
                {' '}
                {data.last_login_at
                    ? dayjs(data.last_login_at).fromNow()
                    : 'ยังไม่เข้าใช้งาน'}
            </td>
            <td>
                <Flex gap='xs' pos='relative'>
                    <TableEditButton
                        onClick={() => openEditUserModal(data, Number(data.id))}
                    />
                    <TableKeyEditButton
                        onClick={() =>
                            openEditUserPasswordModal(Number(data.id))
                        }
                    />
                    <TableDeleteButton onClick={openDeleteConfirmModal} />
                </Flex>
            </td>
        </tr>
    )
}

export default AllUser
